import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Check, Shield, Download, ArrowLeft } from 'lucide-react';
import HubLayout from '../components/common/HubLayout';
import { axiosInstance } from '../services/api';
import SecureLS from 'secure-ls';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";

export default function DeleteAccount() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=preview, 2=confirm, 3=done
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [anonymizeContributions, setAnonymizeContributions] = useState(false);

  useEffect(() => {
    axiosInstance.get('/account/deletion/preview/')
      .then(res => setPreview(res.data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleRequest = async () => {
    setSubmitting(true);
    try {
      const res = await axiosInstance.post('/account/deletion/request/', { anonymize_contributions: anonymizeContributions });
      setResult(res.data);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    // Clear everything and sign out
    try {
      const ls = new SecureLS({ encodingType: 'aes' });
      ls.clear();
    } catch (e) {}
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login';
  };

  if (loading) {
    return (
      <HubLayout activeView="settings">
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 24px', textAlign: 'center', color: '#828A76' }}>Loading...</div>
      </HubLayout>
    );
  }

  const inv = preview?.inventory || {};
  const cons = preview?.consequences || {};
  const hasConsequences = (cons.familiesClosing?.length > 0) || (cons.minorsTransferring?.length > 0);

  // ─── S3: Done ───
  if (step === 3) {
    return (
      <HubLayout activeView="settings">
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 24px' }}>
          <div style={{ background: '#fff', border: '1px solid #CCC', borderRadius: 16, padding: '48px 40px', textAlign: 'center' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#EDF3E0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <Check size={36} color="#4A6120" />
            </div>
            <h1 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 28, color: '#2C2F26', marginBottom: 8 }}>Your account is closed</h1>
            <p style={{ fontSize: 17, color: '#5A6150', fontFamily: FONT_B, marginBottom: 8 }}>
              We've sent a confirmation to <b>{preview?.email}</b>.
            </p>
            <p style={{ fontSize: 15, color: '#828A76', fontFamily: FONT_B, marginBottom: 24 }}>
              Your personal details will be permanently deleted on <b>{result?.purgeAfter ? new Date(result.purgeAfter).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '30 days from now'}</b>.
            </p>
            <div style={{ background: '#F2F2F2', border: '1px solid #CCC', borderRadius: 12, padding: '16px 20px', marginBottom: 28, textAlign: 'left' }}>
              <p style={{ fontSize: 15, color: '#5A6150', fontFamily: FONT_B, margin: 0 }}>
                Your name has already been removed from everything. Your stories, photos, and videos stay with your families — attributed to "A family member."
              </p>
            </div>
            <button onClick={handleDone} style={{
              background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 18,
              padding: '16px 40px', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: FONT_B, minHeight: 56,
            }}>
              Done
            </button>
          </div>
        </div>
      </HubLayout>
    );
  }

  // ─── S2: Confirm ───
  if (step === 2) {
    const checks = [
      { text: 'Your stories, photos, and videos stay with your families', stays: true },
      { text: 'Wisdom moments you shared stay, unattributed', stays: true },
      { text: 'Your name is replaced with "A family member"', stays: true },
      { text: 'Your email, password, and personal details are deleted', stays: false },
      { text: 'You lose access to all your families', stays: false },
      { text: 'Scheduled time capsules are cancelled', stays: false },
    ];

    return (
      <HubLayout activeView="settings">
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 24px' }}>
          <button onClick={() => setStep(1)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#556B2F', fontWeight: 600, fontSize: 15, cursor: 'pointer', fontFamily: FONT_B, padding: 0, marginBottom: 24 }}>
            <ArrowLeft size={16} /> Back
          </button>
          <div style={{ background: '#fff', border: '1px solid #CCC', borderRadius: 16, padding: '36px 32px' }}>
            <h1 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 26, color: '#2C2F26', marginBottom: 16 }}>Are you sure?</h1>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
              {checks.map((c, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 16, fontFamily: FONT_B, color: '#2C2F26' }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: c.stays ? '#F1F5E6' : '#F2F2F2',
                    border: c.stays ? '1.5px solid #C8D3AE' : '1.5px solid #CCC',
                    color: c.stays ? '#556B2F' : '#777', fontWeight: 700, fontSize: 14,
                  }}>
                    {c.stays ? '✓' : '–'}
                  </div>
                  {c.text}
                </div>
              ))}
            </div>

            <div style={{ background: '#F2F2F2', border: '1px solid #CCC', borderRadius: 12, padding: '14px 18px', marginBottom: 28 }}>
              <p style={{ fontSize: 15, color: '#5A6150', fontFamily: FONT_B, margin: 0 }}>
                You have <b>30 days</b> to change your mind. We'll email you a link to bring your account back. After that, your personal data is deleted for good.
              </p>
            </div>

            {/* Anonymize choice */}
            <div style={{ background: '#FBFBF3', border: '1px solid #E6E4D8', borderRadius: 12, padding: '16px 18px', marginBottom: 16 }}>
              <p style={{ fontWeight: 700, fontSize: 15, color: '#2C2F26', fontFamily: FONT_B, marginBottom: 12 }}>What happens to what you shared?</p>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 10 }}>
                <input type="radio" name="anon" checked={!anonymizeContributions} onChange={() => setAnonymizeContributions(false)} style={{ marginTop: 3, accentColor: '#556B2F' }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#2C2F26', fontFamily: FONT_B }}>Leave my name on what I shared</div>
                  <div style={{ fontSize: 14, color: '#828A76', fontFamily: FONT_B }}>The family keeps the stories as you wrote them</div>
                </div>
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input type="radio" name="anon" checked={anonymizeContributions} onChange={() => setAnonymizeContributions(true)} style={{ marginTop: 3, accentColor: '#556B2F' }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#2C2F26', fontFamily: FONT_B }}>Remove my name from what I shared</div>
                  <div style={{ fontSize: 14, color: '#828A76', fontFamily: FONT_B }}>Everything survives; your name is replaced with "A family member"</div>
                </div>
              </label>
            </div>

            {/* Disclosure lines */}
            <div style={{ background: '#F2F2F2', border: '1px solid #CCC', borderRadius: 12, padding: '14px 18px', marginBottom: 16 }}>
              <p style={{ fontSize: 15, color: '#5A6150', fontFamily: FONT_B, margin: '0 0 8px' }}>
                <strong>Your name stays on the family tree.</strong> A relative can remove a tree-only person if they choose to.
              </p>
              <p style={{ fontSize: 15, color: '#5A6150', fontFamily: FONT_B, margin: '0 0 8px' }}>
                <strong>Your family will still be able to tell it was you.</strong> In a small family, an unnamed contribution is identified by elimination.
              </p>
              <p style={{ fontSize: 13, color: '#828A76', fontFamily: FONT_B, margin: 0 }}>
                Nothing is deleted from the family's memories. Already-exported copies cannot be recalled. This choice is final once the 30-day grace period ends.
              </p>
            </div>

            {/* Confirmation checkbox */}
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 12, padding: '16px 18px',
              background: confirmed ? '#FDF3EA' : '#FAFAF3', border: `1.5px solid ${confirmed ? '#E0B48F' : '#E4E1D2'}`,
              borderRadius: 12, marginBottom: 20, cursor: 'pointer', transition: 'all .15s',
            }}>
              <input
                type="checkbox"
                checked={confirmed}
                onChange={e => setConfirmed(e.target.checked)}
                style={{ width: 22, height: 22, accentColor: '#B3300F', marginTop: 2, cursor: 'pointer', flexShrink: 0 }}
              />
              <span style={{ fontSize: 16, color: '#2C2F26', fontFamily: FONT_B, lineHeight: 1.4 }}>
                I understand that my personal data will be permanently deleted and my name will be removed from all stories, photos, and wisdom moments.
              </span>
            </label>

            {error && <p style={{ color: '#C0392B', fontSize: 14, marginBottom: 12 }}>{error}</p>}

            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={handleRequest} disabled={!confirmed || submitting} style={{
                background: confirmed && !submitting ? '#B3300F' : '#E4E2D6',
                color: confirmed && !submitting ? '#fff' : '#A7A996',
                fontWeight: 700, fontSize: 17,
                padding: '16px 32px', borderRadius: 12, border: 'none',
                cursor: confirmed && !submitting ? 'pointer' : 'not-allowed',
                fontFamily: FONT_B, minHeight: 56, flex: 1, transition: 'all .15s',
              }}>
                {submitting ? 'Closing...' : 'Close my account'}
              </button>
              <button onClick={() => setStep(1)} style={{
                background: 'transparent', color: '#666', fontWeight: 600, fontSize: 15,
                padding: '16px 24px', borderRadius: 12, border: '1px solid #CCC', cursor: 'pointer', fontFamily: FONT_B, minHeight: 56,
              }}>
                Never mind
              </button>
            </div>
          </div>
        </div>
      </HubLayout>
    );
  }

  // ─── S1: Preview ───
  return (
    <HubLayout activeView="settings">
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 24px' }}>
        <button onClick={() => navigate('/settings')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#556B2F', fontWeight: 600, fontSize: 15, cursor: 'pointer', fontFamily: FONT_B, padding: 0, marginBottom: 24 }}>
          <ArrowLeft size={16} /> Settings
        </button>

        <div style={{ background: '#fff', border: '1px solid #CCC', borderRadius: 16, padding: '36px 32px' }}>
          <h1 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 28, color: '#2C2F26', marginBottom: 4 }}>Close your account</h1>
          <p style={{ fontSize: 16, color: '#666', fontFamily: FONT_B, marginBottom: 24 }}>Here's what will happen to your content.</p>

          {/* Tally */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
            {[
              { n: inv.stories, l: 'Stories' },
              { n: inv.mediaFiles, l: 'Photos & Videos' },
              { n: inv.wisdomMoments, l: 'Wisdom Moments' },
              { n: inv.families, l: 'Families' },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: FONT_H, fontWeight: 600, fontSize: 30, color: '#4A5D23' }}>{s.n || 0}</div>
                <div style={{ fontSize: 14.5, color: '#666', fontFamily: FONT_B }}>{s.l}</div>
              </div>
            ))}
          </div>

          {/* Rail: Stays */}
          <div style={{ background: '#F2F2F2', border: '1px solid #CCC', borderLeft: '4px solid #556B2F', borderRadius: 14, padding: '16px 20px', marginBottom: 14 }}>
            <h3 style={{ fontWeight: 700, fontSize: 16, color: '#556B2F', marginBottom: 8, fontFamily: FONT_B }}>Stays with your families</h3>
            {['Your stories, photos, videos, and voice recordings', 'Wisdom moments you shared', 'Notes and reactions from family members'].map((t, i) => (
              <p key={i} style={{ fontSize: 15, color: '#666', margin: '4px 0', fontFamily: FONT_B }}>{t}</p>
            ))}
          </div>

          {/* Rail: Comes off */}
          <div style={{ background: '#F2F2F2', border: '1px solid #CCC', borderLeft: '4px solid #E65100', borderRadius: 14, padding: '16px 20px', marginBottom: 14 }}>
            <h3 style={{ fontWeight: 700, fontSize: 16, color: '#E65100', marginBottom: 8, fontFamily: FONT_B }}>Comes off everything</h3>
            {['Your name — replaced with "A family member"', 'Your profile photo', 'Your email, phone, and personal details'].map((t, i) => (
              <p key={i} style={{ fontSize: 15, color: '#666', margin: '4px 0', fontFamily: FONT_B }}>{t}</p>
            ))}
          </div>

          {/* Rail: Stops */}
          <div style={{ background: '#F2F2F2', border: '1px solid #CCC', borderLeft: '4px dashed #999', borderRadius: 14, padding: '16px 20px', marginBottom: 20 }}>
            <h3 style={{ fontWeight: 700, fontSize: 16, color: '#666', marginBottom: 8, fontFamily: FONT_B }}>Stops</h3>
            {['Scheduled time capsules are cancelled', 'You lose access to all your families', 'Your sign-in no longer works'].map((t, i) => (
              <p key={i} style={{ fontSize: 15, color: '#666', margin: '4px 0', fontFamily: FONT_B }}>{t}</p>
            ))}
          </div>

          {/* Consequences */}
          {hasConsequences && (
            <div style={{ background: '#FDF3EA', border: '1.5px solid #E0B48F', borderRadius: 14, padding: '16px 20px', marginBottom: 20 }}>
              <h3 style={{ fontWeight: 700, fontSize: 16, color: '#B34700', marginBottom: 8, fontFamily: FONT_B }}>
                <AlertTriangle size={16} style={{ verticalAlign: '-3px' }} /> Important consequences
              </h3>
              {cons.familiesClosing?.map((f, i) => (
                <p key={`fc-${i}`} style={{ fontSize: 15, color: '#7A4A00', margin: '4px 0', fontFamily: FONT_B }}>
                  <b>{f}</b> will be closed — you're the last adult member.
                </p>
              ))}
              {cons.minorsTransferring?.map((m, i) => (
                <p key={`mt-${i}`} style={{ fontSize: 15, color: '#7A4A00', margin: '4px 0', fontFamily: FONT_B }}>
                  Guardianship of <b>{m.name}</b> transfers to <b>{m.newGuardianName}</b>.
                </p>
              ))}
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button disabled style={{
              background: '#E4E2D6', color: '#A7A996', fontWeight: 700, fontSize: 16,
              padding: '16px 24px', borderRadius: 12, border: 'none', cursor: 'not-allowed',
              fontFamily: FONT_B, minHeight: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
              <Download size={18} /> Download everything first
              <span style={{ background: '#D4D0C0', color: '#8A8A7A', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, marginLeft: 4 }}>Coming soon</span>
            </button>
            <button onClick={() => setStep(2)} style={{
              background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 17,
              padding: '16px 32px', borderRadius: 12, border: 'none', cursor: 'pointer',
              fontFamily: FONT_B, minHeight: 56,
            }}>
              Continue
            </button>
            <button onClick={() => navigate('/settings')} style={{
              background: 'transparent', color: '#666', fontWeight: 600, fontSize: 15,
              padding: '12px 24px', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: FONT_B,
            }}>
              Never mind, keep my account
            </button>
          </div>
        </div>
      </div>
    </HubLayout>
  );
}
